/**
 * The words for an education entry's degree — one mapping for the Education
 * form's options and for every card that shows the entry (journey-web Settings
 * → Profile, business-web's talent profile).
 *
 * A LITERAL `t()` per degree, so the key-usage gate proves each exists, in a
 * `switch` exhaustive over `EducationDegree`: a new option is a compile error
 * here rather than a raw slug ("bachelor") on screen in every locale.
 */
import { isEducationDegree, type EducationDegree } from '@tonnie/types';

type Translate = (key: string, options?: Record<string, unknown>) => string;

export function educationDegreeLabel(t: Translate, degree: EducationDegree): string {
  switch (degree) {
    case 'bachelor':
      return t('onboarding.education.bachelor');
    case 'master':
      return t('onboarding.education.master');
    case 'phd':
      return t('onboarding.education.phd');
    case 'other':
      return t('common.other');
    default: {
      const unhandled: never = degree;
      return unhandled;
    }
  }
}

/**
 * The line that heads an education entry, or `null` when there is nothing to
 * say. A known degree is translated; a free-text degree (from a CV import) is
 * shown as written. The degree and the field are joined by the locale's own
 * phrasing (`profile.degreeInField`), never by gluing words together. "Other"
 * says nothing a field of study does not say better, so with a field it gives
 * way to the field alone.
 */
export function educationHeadline(
  t: Translate,
  entry: { degree?: string | null; field?: string | null }
): string | null {
  const stored = entry.degree?.trim() ?? '';
  const field = entry.field?.trim() ?? '';

  if (stored === 'other') return field || educationDegreeLabel(t, 'other');
  const degree = isEducationDegree(stored) ? educationDegreeLabel(t, stored) : stored;

  if (degree && field) return t('profile.degreeInField', { degree, field });
  return degree || field || null;
}
